/**
 * Multi-Standort Management
 * 
 * Features:
 * - Filialen verwalten
 * - Standort-Vergleich
 * - Zentrale Übersicht
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Plus, Building2, TrendingUp, Edit2, Trash2, Save,
  RefreshCw, BarChart3, X, Check, Phone, Mail, Clock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LocationsManagement() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  
  const [locations, setLocations] = useState([]);
  const [comparison, setComparison] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    is_headquarters: false
  });

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    loadData();
  }, [sessionToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      const [locRes, compRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/locations`, { headers }),
        fetch(`${BACKEND_URL}/api/locations/reports/comparison?period=month`, { headers })
      ]);
      
      if (locRes.ok) {
        const data = await locRes.json();
        setLocations(data.locations || []);
      }
      
      if (compRes.ok) {
        setComparison(await compRes.json());
      }
      
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      postal_code: '',
      phone: '',
      email: '',
      is_headquarters: false
    });
    setShowModal(true);
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      postal_code: location.postal_code || '',
      phone: location.phone || '',
      email: location.email || '',
      is_headquarters: location.is_headquarters || false
    });
    setShowModal(true);
  };

  const saveLocation = async () => {
    try {
      const url = editingLocation 
        ? `${BACKEND_URL}/api/locations/${editingLocation.location_id}`
        : `${BACKEND_URL}/api/locations`;
      
      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingLocation ? 'Standort aktualisiert' : 'Standort erstellt');
        setShowModal(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteLocation = async (location) => {
    if (!confirm(`Standort "${location.name}" wirklich deaktivieren?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/locations/${location.location_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        toast.success('Standort deaktiviert');
        loadData();
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Header */}
      <header className="bg-[#111C32] border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-400" />
              Multi-Standort
            </h1>
            <p className="text-sm text-slate-400">
              Alle Filialen zentral verwalten
            </p>
          </div>
          
          <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Standort
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700 flex-wrap">
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="comparison">Vergleich</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            {locations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map((location) => (
                  <Card key={location.location_id} className="bg-[#111C32] border-slate-700">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-white flex items-center gap-2">
                            {location.name}
                            {location.is_headquarters && (
                              <Badge variant="secondary" className="bg-amber-500/20 text-amber-400">
                                Zentrale
                              </Badge>
                            )}
                          </CardTitle>
                          {location.city && (
                            <CardDescription className="text-slate-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {location.city}
                            </CardDescription>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(location)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteLocation(location)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Stats */}
                      {location.stats && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-slate-800/50 rounded p-3 text-center">
                            <p className="text-2xl font-bold text-white">{location.stats.orders_today}</p>
                            <p className="text-xs text-slate-400">Bestellungen heute</p>
                          </div>
                          <div className="bg-slate-800/50 rounded p-3 text-center">
                            <p className="text-2xl font-bold text-emerald-400">€{location.stats.revenue_today}</p>
                            <p className="text-xs text-slate-400">Umsatz heute</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Contact */}
                      <div className="space-y-1 text-sm text-slate-400">
                        {location.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-3 h-3" /> {location.phone}
                          </p>
                        )}
                        {location.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="w-3 h-3" /> {location.email}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-4">Noch keine Standorte angelegt</p>
                  <Button onClick={openCreateModal}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Standort erstellen
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* COMPARISON TAB */}
          <TabsContent value="comparison">
            {comparison && comparison.locations?.length > 0 ? (
              <Card className="bg-[#111C32] border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Standort-Vergleich (letzter Monat)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Standort</TableHead>
                          <TableHead className="text-slate-400 text-right">Bestellungen</TableHead>
                          <TableHead className="text-slate-400 text-right">Umsatz</TableHead>
                          <TableHead className="text-slate-400 text-right">Ø Bon</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {comparison.locations.map((loc, idx) => (
                          <TableRow key={loc.location_id} className="border-slate-700">
                            <TableCell className="text-white font-medium">
                              {idx === 0 && <span className="mr-2">🥇</span>}
                              {idx === 1 && <span className="mr-2">🥈</span>}
                              {idx === 2 && <span className="mr-2">🥉</span>}
                              {loc.name}
                              {loc.city && <span className="text-slate-500 ml-2">({loc.city})</span>}
                            </TableCell>
                            <TableCell className="text-right text-slate-300">{loc.total_orders}</TableCell>
                            <TableCell className="text-right text-emerald-400 font-bold">€{loc.total_revenue}</TableCell>
                            <TableCell className="text-right text-slate-300">€{loc.avg_order_value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Totals */}
                  <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">{comparison.totals?.location_count}</p>
                      <p className="text-sm text-slate-400">Standorte</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{comparison.totals?.total_orders}</p>
                      <p className="text-sm text-slate-400">Bestellungen gesamt</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-400">€{comparison.totals?.total_revenue}</p>
                      <p className="text-sm text-slate-400">Umsatz gesamt</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Keine Vergleichsdaten verfügbar</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#111C32] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingLocation ? 'Standort bearbeiten' : 'Neuer Standort'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="z.B. Filiale Hamburg"
                className="bg-slate-800 border-slate-600 text-white mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Stadt</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">PLZ</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="bg-slate-800 border-slate-600 text-white mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">E-Mail</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-800 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600">
              Abbrechen
            </Button>
            <Button onClick={saveLocation} disabled={!formData.name}>
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
